import React, { useId } from "react";
import { AlertCircle } from "lucide-react";
import { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "../CustomFormField/CustomFormField.css";
import "./CustomToggleSwitch.css";

/**
 * ToggleSwitch
 * ------------
 * Props (all except onChange are optional — nothing renders/space is
 * reserved unless you pass it):
 *  - label, description   optional text row to the left of the switch
 *  - isRequired            red "*" next to the label
 *  - colClass              optional bootstrap column wrapper, e.g. "col-md-4"
 *  - className / style     merged onto the outer wrapper
 *  - checked (bool)        controlled state
 *  - onChange(nextChecked) called with the new boolean on toggle
 *  - disabled (bool)
 *  - size                  "sm" | "md" | "lg"  (default "md")
 *  - variant               "pill" | "icon" | "labeled"  (default "pill")
 *  - onLabel / offLabel    text shown inside the track for variant="labeled"
 *  - name                  used for aria-label fallback
 */
const CustomToggleSwitch = ({
  label,
  description,
  isRequired,
  colClass,
  className,
  style,
  checked = false,
  onChange,
  disabled = false,
  size = "md",
  variant = "pill",
  onLabel = "ON",
  offLabel = "OFF",
  name,
  error,
  hint,
}) => {
  const autoId = useId();
  // not `name`: table rows often repeat the same name, and ids must be unique
  const switchId = `tsw-${autoId}`;
  const ids = getFieldIds(switchId);

  const handleToggle = () => {
    if (disabled) return;
    onChange?.(!checked);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleToggle();
    }
  };

 const control = (
  <div className={`tsw-group${className ? ` ${className}` : ""}`} style={style}>
    {label && (
      <label htmlFor={switchId} className="tsw-label">
        {label}
        {isRequired && <span className="tsw-required">*</span>}
      </label>
    )}

    <div className="tsw-controlRow">
      <button
        type="button"
        id={switchId}
        role="switch"
        aria-checked={checked}
        aria-label={label || name}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-invalid={Boolean(error)}
        aria-describedby={ids.describedBy(error, hint)}
        className={`tsw-track tsw-${size} tsw-variant-${variant}${checked ? " tsw-on" : ""}${
          disabled ? " tsw-disabled" : ""
        }${error ? " tsw-error" : ""}`}
      >
        {variant === "labeled" && (
          <span className="tsw-trackText">{checked ? onLabel : offLabel}</span>
        )}

        <span className="tsw-thumb">
          {variant === "icon" && (
            <svg viewBox="0 0 24 24" className="tsw-thumbIcon" aria-hidden="true">
              {checked ? (
                <path d="M5 12.5 9.5 17 19 7" />
              ) : (
                <path d="M6 6l12 12M18 6 6 18" />
              )}
            </svg>
          )}
        </span>
      </button>

      {description && <span className="tsw-desc">{description}</span>}
    </div>

    {error ? (
      <span id={ids.errorId} className="cff-error" role="alert">
        <AlertCircle size={13} aria-hidden="true" />
        <span>{error}</span>
      </span>
    ) : (
      hint && <span id={ids.hintId} className="cff-hint">{hint}</span>
    )}
  </div>
);

  return colClass ? <div className={colClass}>{control}</div> : control;
};

export default React.memo(CustomToggleSwitch);